import * as React from "react";

import { savePreferences } from "api/profile";
import { useAuth } from "hooks/useAuth";
import { useTheme, type Theme } from "./ThemeContext";

// A theme picked while signed out (on the sign-in pages) is saved to the account at the next
// sign-in instead of being replaced by the account's older choice.
const PENDING_KEY = "sap-theme-pending";

const readPending = (): boolean => {
  try {
    return window.localStorage.getItem(PENDING_KEY) === "1";
  } catch (err) {
    return false;
  }
};

const writePending = (pending: boolean) => {
  try {
    if (pending) {
      window.localStorage.setItem(PENDING_KEY, "1");
    } else {
      window.localStorage.removeItem(PENDING_KEY);
    }
  } catch (err) {
    // Storage blocked: the choice still applies in this browser.
  }
};

/**
 * Keeps the theme and the signed-in account in step (user.themePreference):
 * - signing in applies the account's theme, unless one was just picked on the sign-in page,
 *   which is saved instead; an account without a theme takes this browser's;
 * - switching the theme while signed in saves it to the account.
 */
export const ThemeSync: React.FC = () => {
  const { user, applyProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const userId = user?._id || null;
  const accountTheme = user?.themePreference || null;
  const saved = React.useRef<Theme | null>(null);

  const save = React.useCallback(
    async (next: Theme) => {
      if (saved.current === next) {
        return;
      }
      saved.current = next;
      try {
        applyProfile(await savePreferences({ theme: next }));
        writePending(false);
      } catch (err) {
        // Not fatal: the theme still applies here and is saved on the next switch.
        saved.current = null;
      }
    },
    [applyProfile]
  );

  // Signing in (or the account's choice changing elsewhere).
  React.useEffect(() => {
    if (!userId) {
      saved.current = null;
      return;
    }
    if (readPending() || !accountTheme) {
      save(theme);
      return;
    }
    saved.current = accountTheme;
    if (accountTheme !== theme) {
      setTheme(accountTheme);
    }
    // Only when the account or its stored theme changes; theme switches are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, accountTheme]);

  // Switching the theme.
  const previous = React.useRef<Theme>(theme);
  React.useEffect(() => {
    if (previous.current === theme) {
      return;
    }
    previous.current = theme;
    if (!userId) {
      writePending(true);
    } else if (theme !== accountTheme) {
      save(theme);
    }
    // Only when the theme changes; signing in is handled above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  return null;
};

export default ThemeSync;
